import '@ncdai/react-wheel-picker/style.css';

import type { ComponentProps } from 'react';
import * as WheelPickerPrimitive from '@ncdai/react-wheel-picker';

import { cn } from '../../lib/utils';

/**
 * The @ncdai/react-wheel-picker registry item (`shadcn add @ncdai/wheel-picker`),
 * rewritten for this project: its own styles are Tailwind v4 (`*:data-rwp:`,
 * `inset-ring-*`, bare `data-*` variants) on hard-coded zinc, and we are on v3
 * with themed tokens, so the same look is spelled with arbitrary variants and
 * `bg-muted`/`text-muted-foreground` instead.
 */
function WheelPickerWrapper({
  className,
  ...props
}: ComponentProps<typeof WheelPickerPrimitive.WheelPickerWrapper>) {
  return (
    <WheelPickerPrimitive.WheelPickerWrapper
      className={cn(
        'w-56 rounded-lg border bg-background px-1 shadow-sm',
        // The highlight band spans each wheel edge to edge, so the outer two
        // follow the box's own corners.
        '[&>[data-rwp]:first-child>[data-rwp-highlight-wrapper]]:rounded-l-md',
        '[&>[data-rwp]:last-child>[data-rwp-highlight-wrapper]]:rounded-r-md',
        className
      )}
      {...props}
    />
  );
}

function WheelPicker<T extends WheelPickerPrimitive.WheelPickerValue = string>({
  classNames,
  ...props
}: WheelPickerPrimitive.WheelPickerProps<T>) {
  return (
    <WheelPickerPrimitive.WheelPicker
      classNames={{
        optionItem: cn('text-muted-foreground data-[disabled]:opacity-40', classNames?.optionItem),
        highlightWrapper: cn(
          'bg-muted text-foreground',
          'data-[rwp-focused]:ring-2 data-[rwp-focused]:ring-inset data-[rwp-focused]:ring-ring',
          classNames?.highlightWrapper
        ),
        highlightItem: cn('data-[disabled]:opacity-40', classNames?.highlightItem),
      }}
      {...props}
    />
  );
}

export { WheelPicker, WheelPickerWrapper };
export type WheelPickerClassNames = WheelPickerPrimitive.WheelPickerClassNames;
export type WheelPickerOption<T extends WheelPickerPrimitive.WheelPickerValue = string> =
  WheelPickerPrimitive.WheelPickerOption<T>;
export type WheelPickerValue = WheelPickerPrimitive.WheelPickerValue;
